import React, { useState, useEffect, useRef } from 'react';
import { Send, Plus, MessageSquare, Paperclip, User, Bot, Trash2, Edit3, Menu, X, FileText, Image, File, Eye, ExternalLink } from 'lucide-react';
import FormattedMessage from '../components/FormattedMessage';

// Helper function to validate if a file object is valid
const isValidFile = (file) => {
	return file &&
		file.name &&
		file.name.trim() !== '' &&
		file.name !== 'Unknown file' &&
		(file.id || file.size !== undefined);
};

// Helper function to get valid files from a files array
const getValidFiles = (files) => {
	if (!Array.isArray(files)) return [];
	return files.filter(isValidFile);
};

const FilePreviewComponent = ({ file, onRemove, onView }) => {
	const isImage = file.type?.startsWith('image/');
	const isText = file.type?.startsWith('text/') ||
		/\.(txt|md|json|csv|xml|log|sql|py|js|html|css|php)$/i.test(file.name || '');
	const isPDF = file.type === 'application/pdf';

	const formatFileSize = (bytes) => {
		if (!bytes) return '0 B';
		const k = 1024;
		const sizes = ['B', 'KB', 'MB', 'GB'];
		const i = Math.floor(Math.log(bytes) / Math.log(k));
		return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
	};

	const getFileIcon = () => {
		if (isImage) return Image;
		if (isText) return FileText;
		if (isPDF) return File;
		return File;
	};

	const getFileColor = () => {
		if (isImage) return 'text-green-400 bg-green-400/20';
		if (isText) return 'text-blue-400 bg-blue-400/20';
		if (isPDF) return 'text-red-400 bg-red-400/20';
		return 'text-gray-400 bg-gray-400/20';
	};

	const FileIcon = getFileIcon();

	return (
		<div className="flex items-center gap-3 bg-slate-700/50 p-3 rounded-lg border border-slate-600/50">
			<div className={`p-2 rounded-lg ${getFileColor()}`}>
				<FileIcon size={16} />
			</div>
			<div className="flex-1 min-w-0">
				<p className="text-sm font-medium text-white truncate">{file.name}</p>
				<p className="text-xs text-slate-400">{formatFileSize(file.size)}</p>
				{isImage && <p className="text-xs text-green-400">Image • Can be analyzed</p>}
				{isText && !isImage && <p className="text-xs text-blue-400">Text • Can be read</p>}
				{isPDF && <p className="text-xs text-red-400">PDF • Can be processed</p>}
			</div>
			<div className="flex gap-1">
				{(isImage || isPDF) && (
					<button
						onClick={() => onView(file)}
						className="p-1 text-slate-400 hover:text-white transition-colors"
						title="Preview"
					>
						<Eye size={14} />
					</button>
				)}
				<button
					onClick={() => onRemove(file.id)}
					className="p-1 text-red-400 hover:text-red-300 transition-colors"
					title="Remove"
				>
					<X size={14} />
				</button>
			</div>
		</div>
	);
};

const MessageFileComponent = ({ file, onView }) => {
	// Don't render if file is invalid
	if (!isValidFile(file)) {
		return null;
	}

	const isImage = file.type?.startsWith('image/');
	const isText = file.type?.startsWith('text/') ||
		/\.(txt|md|json|csv|xml|log|sql|py|js|html|css|php)$/i.test(file.name || '');
	const isPDF = file.type === 'application/pdf';

	const formatFileSize = (bytes) => {
		if (!bytes) return '';
		const k = 1024;
		const sizes = ['B', 'KB', 'MB', 'GB'];
		const i = Math.floor(Math.log(bytes) / Math.log(k));
		return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
	};

	const getFileIcon = () => {
		if (isImage) return Image;
		if (isText) return FileText;
		if (isPDF) return File;
		return File;
	};

	const getFileColor = () => {
		if (isImage) return 'text-green-400';
		if (isText) return 'text-blue-400';
		if (isPDF) return 'text-red-400';
		return 'text-gray-400';
	};

	const FileIcon = getFileIcon();

	return (
		<div
			className="flex items-center gap-2 bg-slate-700/30 p-2 rounded-lg border border-slate-600/30 cursor-pointer hover:bg-slate-700/50 transition-colors"
			onClick={() => onView && onView(file)}
		>
			<div className={`${getFileColor()}`}>
				<FileIcon size={14} />
			</div>
			<div className="flex-1 min-w-0">
				<p className="text-xs font-medium text-white truncate">{file.name}</p>
				{file.size && (
					<p className="text-xs text-slate-400">{formatFileSize(file.size)}</p>
				)}
			</div>
			{(isImage || isPDF) && (
				<Eye size={12} className="text-slate-400" />
			)}
		</div>
	);
};

const FileViewerModal = ({ file, isOpen, onClose }) => {
	const isImage = file?.type?.startsWith('image/');
	const isPDF = file?.type === 'application/pdf';

	if (!isOpen || !file) return null;

	const handleOpenExternal = () => {
		if (file.uri) {
			window.open(file.uri, '_blank');
		}
	};

	return (
		<div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
			<div className="bg-slate-800 rounded-xl max-w-4xl max-h-[90vh] w-full flex flex-col">
				{/* Header */}
				<div className="flex items-center justify-between p-4 border-b border-slate-700">
					<div className="flex-1 min-w-0">
						<h3 className="text-lg font-semibold text-white truncate">{file.name}</h3>
						<p className="text-sm text-slate-400">
							{file.type} • {file.size ? (file.size / 1024 / 1024).toFixed(2) + ' MB' : 'Unknown size'}
						</p>
					</div>
					<button
						onClick={onClose}
						className="p-2 text-slate-400 hover:text-white transition-colors"
					>
						<X size={20} />
					</button>
				</div>

				{/* Content */}
				<div className="flex-1 p-4 overflow-auto">
					{isImage ? (
						<img
							src={file.uri || URL.createObjectURL(file.file)}
							alt={file.name}
							className="max-w-full max-h-full mx-auto object-contain"
						/>
					) : (
						<div className="flex flex-col items-center justify-center h-full text-center">
							<div className="w-20 h-20 bg-slate-700 rounded-full flex items-center justify-center mb-4">
								<File size={40} className="text-slate-400" />
							</div>
							<h4 className="text-xl font-semibold text-white mb-2">{file.name}</h4>
							<p className="text-slate-400 mb-6">
								{isPDF ? 'PDF Document' : 'File'}
							</p>
							<button
								onClick={handleOpenExternal}
								className="flex items-center gap-2 bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg transition-colors"
							>
								<ExternalLink size={16} />
								Open in Browser
							</button>
						</div>
					)}
				</div>
			</div>
		</div>
	);
};

const AIChatApp = () => {
	const [conversations, setConversations] = useState([]);
	const [currentConvId, setCurrentConvId] = useState(null);
	const [messages, setMessages] = useState([]);
	const [input, setInput] = useState('');
	const [loading, setLoading] = useState(false);
	const [apiKey, setApiKey] = useState('');
	const [showApiKeyInput, setShowApiKeyInput] = useState(false);
	const [uploadedFiles, setUploadedFiles] = useState([]);
	const [sidebarOpen, setSidebarOpen] = useState(false);
	const [fileViewerOpen, setFileViewerOpen] = useState(false);
	const [selectedFile, setSelectedFile] = useState(null);
	const messagesEndRef = useRef(null);
	const fileInputRef = useRef(null);

	useEffect(() => {
		const savedApiKey = localStorage.getItem('anthropic_api_key');
		if (savedApiKey) {
			setApiKey(savedApiKey);
			loadConversations(savedApiKey);
		} else {
			setShowApiKeyInput(true);
		}
	}, []);

	useEffect(() => {
		scrollToBottom();
	}, [messages]);

	const scrollToBottom = () => {
		messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
	};

	const loadConversations = async (apiKey) => {
		if (!apiKey) return;

		try {
			const response = await fetch(`/api/conversations.php?api_key=${encodeURIComponent(apiKey)}`);

			if (!response.ok) {
				throw new Error(`HTTP error! status: ${response.status}`);
			}

			const text = await response.text();
			let data;

			try {
				data = JSON.parse(text);
			} catch (e) {
				console.error('Invalid JSON response:', text);
				throw new Error('Invalid response from server');
			}

			if (data.success) {
				setConversations(data.conversations);
				if (data.conversations.length > 0 && !currentConvId) {
					const firstConv = data.conversations[0];
					setCurrentConvId(firstConv.id);
					loadConversationMessages(firstConv.id, apiKey);
				}
			} else {
				console.error('Error loading conversations:', data.error);
			}
		} catch (error) {
			console.error('Error loading conversations:', error);
		}
	};

	const loadConversationMessages = async (conversationId, apiKey) => {
		if (!apiKey || !conversationId) return;

		try {
			const response = await fetch(`/api/conversations.php?api_key=${encodeURIComponent(apiKey)}&conversation_id=${conversationId}`);

			if (!response.ok) {
				throw new Error(`HTTP error! status: ${response.status}`);
			}

			const text = await response.text();
			let data;

			try {
				data = JSON.parse(text);
			} catch (e) {
				console.error('Invalid JSON response:', text);
				throw new Error('Invalid response from server');
			}

			if (data.success) {
				const formattedMessages = data.messages.map(msg => {
					const message = {
						id: msg.id,
						role: msg.role,
						content: msg.content,
						timestamp: msg.timestamp,
						isError: msg.is_error
					};

					if (msg.files && Array.isArray(msg.files) && msg.files.length > 0) {
						message.files = msg.files.map(file => ({
							...file,
							uri: file.path ? `/api/files.php?file=${encodeURIComponent(file.path)}&api_key=${encodeURIComponent(apiKey)}` : null
						}));
					}

					return message;
				});
				setMessages(formattedMessages);
			} else {
				console.error('Error loading messages:', data.error);
			}
		} catch (error) {
			console.error('Error loading messages:', error);
		}
	};

	const createNewConversation = async () => {
		if (!apiKey) {
			setShowApiKeyInput(true);
			return;
		}

		const newConvId = Date.now().toString();

		try {
			const response = await fetch('/api/conversations.php', {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
				},
				body: JSON.stringify({
					api_key: apiKey,
					conversation_id: newConvId,
					title: 'New Chat'
				})
			});

			if (!response.ok) {
				throw new Error(`HTTP error! status: ${response.status}`);
			}

			const text = await response.text();
			let data;

			try {
				data = JSON.parse(text);
			} catch (e) {
				console.error('Invalid JSON response:', text);
				throw new Error('Invalid response from server');
			}

			if (data.success) {
				setCurrentConvId(newConvId);
				setMessages([]);
				setUploadedFiles([]);
				setSidebarOpen(false);
				await loadConversations(apiKey);
			} else {
				throw new Error(data.error || 'Failed to create conversation');
			}
		} catch (error) {
			console.error('Error creating conversation:', error);
			setCurrentConvId(newConvId);
			setMessages([]);
			setUploadedFiles([]);
			setSidebarOpen(false);
		}
	};

	const selectConversation = async (convId) => {
		setCurrentConvId(convId);
		if (apiKey) {
			await loadConversationMessages(convId, apiKey);
		}
		setUploadedFiles([]);
		setSidebarOpen(false);
	};

	const updateConversationTitle = async (convId, newTitle) => {
		if (!apiKey) return;

		try {
			const response = await fetch('/api/conversations.php', {
				method: 'PUT',
				headers: {
					'Content-Type': 'application/json',
				},
				body: JSON.stringify({
					api_key: apiKey,
					conversation_id: convId,
					title: newTitle
				})
			});

			const data = await response.json();
			if (data.success) {
				await loadConversations(apiKey);
			}
		} catch (error) {
			console.error('Error updating conversation title:', error);
		}
	};

	const deleteConversation = async (convId) => {
		if (!apiKey) return;

		try {
			const response = await fetch('/api/conversations.php', {
				method: 'DELETE',
				headers: {
					'Content-Type': 'application/json',
				},
				body: JSON.stringify({
					api_key: apiKey,
					conversation_id: convId
				})
			});

			const data = await response.json();
			if (data.success) {
				if (currentConvId === convId) {
					const remainingConvs = conversations.filter(conv => conv.id !== convId);
					if (remainingConvs.length > 0) {
						setCurrentConvId(remainingConvs[0].id);
						await loadConversationMessages(remainingConvs[0].id, apiKey);
					} else {
						setCurrentConvId(null);
						setMessages([]);
					}
				}
				await loadConversations(apiKey);
			}
		} catch (error) {
			console.error('Error deleting conversation:', error);
		}
	};

	const handleFileUpload = (e) => {
		const files = Array.from(e.target.files);
		const newFiles = files.map(file => ({
			id: Date.now() + Math.random(),
			name: file.name,
			size: file.size,
			type: file.type,
			file: file
		}));
		setUploadedFiles(prev => [...prev, ...newFiles]);
	};

	const removeFile = (fileId) => {
		setUploadedFiles(prev => prev.filter(f => f.id !== fileId));
	};

	const viewFile = (file) => {
		setSelectedFile(file);
		setFileViewerOpen(true);
	};

	const closeFileViewer = () => {
		setFileViewerOpen(false);
		setSelectedFile(null);
	};

	const saveApiKey = async () => {
		localStorage.setItem('anthropic_api_key', apiKey);
		setShowApiKeyInput(false);
		await loadConversations(apiKey);
	};

	const sendMessage = async () => {
		if (!input.trim() && uploadedFiles.length === 0) return;
		if (!apiKey) {
			setShowApiKeyInput(true);
			return;
		}
		if (!currentConvId) {
			await createNewConversation();
			return;
		}

		const messageId = Date.now().toString();
		const userMessage = {
			id: messageId,
			role: 'user',
			content: input,
			timestamp: new Date().toISOString()
		};

		if (uploadedFiles.length > 0) {
			userMessage.files = [...uploadedFiles];
		}

		const newMessages = [...messages, userMessage];
		setMessages(newMessages);
		const currentInput = input;
		const currentFiles = [...uploadedFiles];
		setInput('');
		setUploadedFiles([]);
		setLoading(true);

		try {
			const formData = new FormData();
			formData.append('message', currentInput);
			formData.append('api_key', apiKey);
			formData.append('conversation_id', currentConvId);
			formData.append('message_id', messageId);

			if (currentFiles.length > 0) {
				currentFiles.forEach((fileObj, index) => {
					formData.append(`files[${index}]`, fileObj.file);
				});
			}

			const response = await fetch('/api/chat.php', {
				method: 'POST',
				body: formData
			});

			if (!response.ok) {
				throw new Error(`HTTP error! status: ${response.status}`);
			}

			const text = await response.text();
			let data;

			try {
				data = JSON.parse(text);
			} catch (e) {
				console.error('Invalid JSON response:', text);
				throw new Error('Invalid response from server');
			}

			if (!data.success) {
				throw new Error(data.error || 'Unknown error occurred');
			}

			if (apiKey && currentConvId) {
				await loadConversationMessages(currentConvId, apiKey);
			}

			const currentConv = conversations.find(c => c.id === currentConvId);
			if (currentConv && currentConv.title === 'New Chat' && currentInput.length > 0) {
				const title = currentInput.slice(0, 50) + (currentInput.length > 50 ? '...' : '');
				await updateConversationTitle(currentConvId, title);
			}

			if (apiKey) {
				await loadConversations(apiKey);
			}

		} catch (error) {
			console.error('Error:', error);

			const errorMessage = {
				id: (Date.now() + 1).toString(),
				role: 'assistant',
				content: `Error: ${error.message}`,
				timestamp: new Date().toISOString(),
				isError: true
			};
			setMessages([...newMessages, errorMessage]);
		} finally {
			setLoading(false);
		}
	};

	const handleKeyPress = (e) => {
		if (e.key === 'Enter' && !e.shiftKey) {
			e.preventDefault();
			sendMessage();
		}
	};

	if (showApiKeyInput) {
		return (
			<div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 p-4">
				<div className="bg-white/10 backdrop-blur-lg p-8 rounded-2xl shadow-2xl w-full max-w-md border border-white/20">
					<div className="text-center mb-6">
						<div className="w-16 h-16 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full mx-auto mb-4 flex items-center justify-center">
							<Bot size={32} className="text-white" />
						</div>
						<h2 className="text-2xl font-bold text-white mb-2">Welcome to AI Chat</h2>
						<p className="text-slate-300">Enter your Anthropic API key to get started</p>
					</div>
					<input
						type="password"
						value={apiKey}
						onChange={(e) => setApiKey(e.target.value)}
						placeholder="sk-ant-..."
						className="w-full p-4 border border-white/20 rounded-xl mb-4 bg-white/10 backdrop-blur-sm text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
					/>
					<button
						onClick={saveApiKey}
						className="w-full bg-gradient-to-r from-purple-500 to-pink-500 text-white p-4 rounded-xl hover:from-purple-600 hover:to-pink-600 transition-all duration-200 font-semibold shadow-lg"
					>
						Save API Key
					</button>
				</div>
			</div>
		);
	}

	return (
		<div className="flex h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
			{/* Mobile Overlay */}
			{sidebarOpen && (
				<div
					className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden"
					onClick={() => setSidebarOpen(false)}
				/>
			)}

			{/* Sidebar */}
			<div className={`${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} md:translate-x-0 fixed md:static z-50 w-80 md:w-72 bg-slate-800/90 backdrop-blur-xl border-r border-slate-700/50 flex flex-col transition-transform duration-300 ease-in-out h-full md:h-auto`}>
				<div className="p-4 border-b border-slate-700/50">
					<div className="flex items-center justify-between mb-4">
						<div className="flex items-center gap-3">
							<div className="w-8 h-8 bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg flex items-center justify-center">
								<Bot size={18} className="text-white" />
							</div>
							<h1 className="text-xl font-bold text-white">AI Chat</h1>
						</div>
						<button
							onClick={() => setSidebarOpen(false)}
							className="md:hidden text-slate-400 hover:text-white"
						>
							<X size={20} />
						</button>
					</div>
					<button
						onClick={createNewConversation}
						className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white p-3 rounded-xl hover:from-purple-600 hover:to-pink-600 transition-all duration-200 shadow-lg"
					>
						<Plus size={18} />
						New Chat
					</button>
				</div>

				<div className="flex-1 overflow-y-auto custom-scrollbar">
					{conversations.map((conv) => (
						<div
							key={conv.id}
							className={`p-4 border-b border-slate-700/30 cursor-pointer hover:bg-slate-700/50 group transition-all duration-200 ${currentConvId === conv.id ? 'bg-gradient-to-r from-purple-500/20 to-pink-500/20 border-purple-500/30' : ''
								}`}
							onClick={() => selectConversation(conv.id)}
						>
							<div className="flex items-center gap-3">
								<MessageSquare size={16} className="text-slate-400 flex-shrink-0" />
								<div className="flex-1 min-w-0">
									<div className="text-sm font-medium truncate text-white">{conv.title}</div>
									<div className="text-xs text-slate-400 mt-1">
										{new Date(conv.created_at).toLocaleDateString()} • {conv.message_count || 0} messages
									</div>
								</div>
								<button
									onClick={(e) => {
										e.stopPropagation();
										deleteConversation(conv.id);
									}}
									className="opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-300 transition-all duration-200 p-1 hover:bg-red-500/20 rounded-lg"
								>
									<Trash2 size={14} />
								</button>
							</div>
						</div>
					))}
				</div>

				<div className="p-4 border-t border-slate-700/50">
					<button
						onClick={() => setShowApiKeyInput(true)}
						className="w-full text-sm text-slate-400 hover:text-white flex items-center justify-center gap-2 p-2 hover:bg-slate-700/50 rounded-lg transition-all duration-200"
					>
						<Edit3 size={16} />
						Update API Key
					</button>
				</div>
			</div>

			{/* Main Chat Area */}
			<div className="flex-1 flex flex-col min-w-0">
				{/* Header */}
				<div className="bg-slate-800/50 backdrop-blur-sm border-b border-slate-700/50 p-4 flex items-center gap-3">
					<button
						onClick={() => setSidebarOpen(true)}
						className="md:hidden text-slate-400 hover:text-white p-2 hover:bg-slate-700/50 rounded-lg"
					>
						<Menu size={20} />
					</button>
					<div className="flex items-center gap-3">
						<div className="w-8 h-8 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-full flex items-center justify-center">
							<Bot size={16} className="text-white" />
						</div>
						<div>
							<h2 className="font-semibold text-white">AI Assistant</h2>
							<p className="text-xs text-slate-400">Always here to help</p>
						</div>
					</div>
				</div>

				{/* Messages */}
				<div className="flex-1 overflow-y-auto p-2 sm:p-4 space-y-4 sm:space-y-6 custom-scrollbar">
					{messages.map((message) => {
						// Get valid files for this message
						const validFiles = getValidFiles(message.files);
						const hasValidFiles = validFiles.length > 0;

						return (
							<div
								key={message.id}
								className={`flex gap-3 ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
							>
								<div
									className={`flex gap-3 max-w-xs sm:max-w-lg md:max-w-2xl lg:max-w-3xl xl:max-w-4xl min-w-0 ${message.role === 'user' ? 'flex-row-reverse' : 'flex-row'}`}
								>
									<div className="flex-shrink-0">
										{message.role === 'user' ? (
											<div className="w-7 h-7 sm:w-8 sm:h-8 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-full flex items-center justify-center shadow-lg">
												<User size={16} className="text-white" />
											</div>
										) : (
											<div className="w-7 h-7 sm:w-8 sm:h-8 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-full flex items-center justify-center shadow-lg">
												<Bot size={16} className="text-white" />
											</div>
										)}
									</div>
									<div
										className={`p-3 sm:p-4 rounded-xl sm:rounded-2xl shadow-lg break-words min-w-0 overflow-hidden ${message.role === 'user'
											? 'bg-gradient-to-r from-blue-500 to-cyan-500 text-white ml-2 sm:ml-4'
											: message.isError
												? 'bg-red-500/20 text-red-200 border border-red-500/30 mr-2 sm:mr-4'
												: 'bg-slate-800/80 backdrop-blur-sm border border-slate-700/50 mr-2 sm:mr-4'
											}`}
									>
										{/* Files for user messages - show above content */}
										{hasValidFiles && message.role === 'user' && (
											<div className="mb-3 pb-3 border-b border-white/20">
												<div className="text-sm opacity-80 mb-2 flex items-center gap-1">
													<Paperclip size={12} />
													{validFiles.length} file{validFiles.length > 1 ? 's' : ''}
												</div>
												<div className="space-y-2">
													{validFiles.map((file, index) => (
														<MessageFileComponent
															key={file.id || `file-${index}`}
															file={file}
															onView={viewFile}
														/>
													))}
												</div>
											</div>
										)}

										{/* Message content */}
										{message.role === 'assistant' ? (
											<FormattedMessage content={message.content} />
										) : (
											<div className="whitespace-pre-wrap leading-relaxed break-words">{message.content}</div>
										)}

										{/* Files for assistant messages - show below content */}
										{hasValidFiles && message.role === 'assistant' && (
											<div className="mt-3 pt-3 border-t border-slate-600/50">
												<div className="text-sm opacity-80 mb-2 flex items-center gap-1">
													<Paperclip size={12} />
													Referenced files
												</div>
												<div className="space-y-2">
													{validFiles.map((file, index) => (
														<MessageFileComponent
															key={file.id || `file-${index}`}
															file={file}
															onView={viewFile}
														/>
													))}
												</div>
											</div>
										)}
									</div>
								</div>
							</div>
						);
					})}
					{loading && (
						<div className="flex justify-start">
							<div className="flex gap-2 sm:gap-3">
								<div className="w-7 h-7 sm:w-8 sm:h-8 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-full flex items-center justify-center shadow-lg">
									<Bot size={16} className="text-white" />
								</div>
								<div className="bg-slate-800/80 backdrop-blur-sm border border-slate-700/50 p-3 sm:p-4 rounded-xl sm:rounded-2xl shadow-lg">
									<div className="flex space-x-2">
										<div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce"></div>
										<div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
										<div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
									</div>
								</div>
							</div>
						</div>
					)}
					<div ref={messagesEndRef} />
				</div>

				{/* Input Area */}
				<div className="border-t border-slate-700/50 p-4 bg-slate-800/30 backdrop-blur-sm">
					{uploadedFiles.length > 0 && (
						<div className="mb-4">
							<div className="flex items-center justify-between mb-2">
								<span className="text-sm text-slate-300">
									{uploadedFiles.length} file{uploadedFiles.length > 1 ? 's' : ''} attached
								</span>
								<button
									onClick={() => setUploadedFiles([])}
									className="text-xs text-slate-400 hover:text-red-400 transition-colors"
								>
									Clear all
								</button>
							</div>
							<div className="space-y-2">
								{uploadedFiles.map((file) => (
									<FilePreviewComponent
										key={file.id}
										file={file}
										onRemove={removeFile}
										onView={viewFile}
									/>
								))}
							</div>
						</div>
					)}
					<div className="flex gap-3 items-end">
						<input
							type="file"
							ref={fileInputRef}
							onChange={handleFileUpload}
							multiple
							className="hidden"
						/>
						<button
							onClick={() => fileInputRef.current?.click()}
							className="p-3 text-slate-400 hover:text-white hover:bg-slate-700/50 rounded-xl transition-all duration-200"
							title="Attach files"
						>
							<Paperclip size={20} />
						</button>
						<div className="flex-1 relative">
							<textarea
								value={input}
								onChange={(e) => setInput(e.target.value)}
								onKeyPress={handleKeyPress}
								placeholder="Type your message..."
								className="w-full p-4 pr-12 bg-slate-700/50 backdrop-blur-sm border border-slate-600/50 rounded-xl resize-none focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-white placeholder-slate-400"
								rows="1"
								style={{ minHeight: '52px', maxHeight: '120px' }}
							/>
						</div>
						<button
							onClick={sendMessage}
							disabled={loading || (!input.trim() && uploadedFiles.length === 0)}
							className="p-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-xl hover:from-purple-600 hover:to-pink-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-lg"
						>
							<Send size={20} />
						</button>
					</div>
				</div>
			</div>

			{/* File Viewer Modal */}
			<FileViewerModal
				file={selectedFile}
				isOpen={fileViewerOpen}
				onClose={closeFileViewer}
			/>

			<style jsx>{`
				.custom-scrollbar {
					scrollbar-width: thin;
					scrollbar-color: #475569 #1e293b;
				}
				
				.custom-scrollbar::-webkit-scrollbar {
					width: 6px;
				}
				
				.custom-scrollbar::-webkit-scrollbar-track {
					background: #1e293b;
				}
				
				.custom-scrollbar::-webkit-scrollbar-thumb {
					background: #475569;
					border-radius: 3px;
				}
				
				.custom-scrollbar::-webkit-scrollbar-thumb:hover {
					background: #64748b;
				}
			`}</style>
		</div>
	);
};

export default AIChatApp;